import {
  TIndex,
  TIndexMapping,
} from '../../../utils/types/common';

export const getIndexMapping = (index: TIndex) => {
  const completeIndexMapping: Record<
    TIndex,
    TIndexMapping
  > = {
    NDVI: {
      index: 'NDVI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Non-vegetated surfaces',
          lowerValue: -1.0,
          upperValue: 0.0,
        },
        {
          label: 'Sparse vegetation',
          lowerValue: 0.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate vegetation',
          lowerValue: 0.2,
          upperValue: 0.5,
          valueColor: '#ffaa00',
        },
        {
          label: 'High vegetation',
          lowerValue: 0.5,
          upperValue: 1.0,
        },
      ],
    },
    EVI: {
      index: 'EVI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Non-vegetated surfaces',
          lowerValue: -1.0,
          upperValue: 0.0,
        },
        {
          label: 'Low vegetation',
          lowerValue: 0.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate vegetation',
          lowerValue: 0.2,
          upperValue: 0.6,
          valueColor: '#ffaa00',
        },
        {
          label: 'High vegetation',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    ARVI: {
      index: 'ARVI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Non-vegetated surfaces',
          lowerValue: -1.0,
          upperValue: 0.0,
        },
        {
          label: 'Low green vegetation',
          lowerValue: 0.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate green vegetation',
          lowerValue: 0.2,
          upperValue: 0.5,
          valueColor: '#ffaa00',
        },
        {
          label: 'Dense vegetation',
          lowerValue: 0.5,
          upperValue: 1.0,
        },
      ],
    },
    CIG: {
      index: 'CIG',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Low chlorophyll content',
          lowerValue: -1.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate chlorophyll content',
          lowerValue: 0.2,
          upperValue: 0.5,
          valueColor: '#ffaa00',
        },
        {
          label: 'High chlorophyll content',
          lowerValue: 0.5,
          upperValue: 1.0,
        },
      ],
    },
    CCCI: {
      index: 'CCCI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Very Low Canopy chlorophyll content',
          lowerValue: -1.0,
          upperValue: 0.0,
        },
        {
          label: 'Low Canopy chlorophyll content',
          lowerValue: 0.0,
          upperValue: 0.3,
        },
        {
          label: 'Moderate canopy chlorophyll content',
          lowerValue: 0.3,
          upperValue: 0.6,
          valueColor: '#ffaa00',
        },
        {
          label: 'High canopy chlorophyll content',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    EGS: {
      index: 'EGS',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Poor early growth',
          lowerValue: -1.0,
          upperValue: 0.3,
        },
        {
          label: 'Moderate early growth',
          lowerValue: 0.3,
          upperValue: 0.6,
          valueColor: '#ffaa00',
        },
        {
          label: 'Good early growth',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    TRUE_GREEN_GAUGE: {
      index: 'TRUE_GREEN_GAUGE',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Poor early growth',
          lowerValue: -1.0,
          upperValue: 0.3,
        },
        {
          label: 'Moderate early growth',
          lowerValue: 0.3,
          upperValue: 0.6,
          valueColor: '#ffaa00',
        },
        {
          label: 'Good early growth',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    PVI: {
      index: 'PVI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Low vigour',
          lowerValue: -1.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate vigour',
          lowerValue: 0.2,
          upperValue: 0.5,
          valueColor: '#ffaa00',
        },
        {
          label: 'High vigour',
          lowerValue: 0.5,
          upperValue: 1.0,
        },
      ],
    },
    ATSAVI: {
      index: 'ATSAVI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Bare soil',
          lowerValue: -1.0,
          upperValue: 0.0,
        },
        {
          label: 'Sparse vegetation',
          lowerValue: 0.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate vegetation cover',
          lowerValue: 0.2,
          upperValue: 0.5,
          valueColor: '#ffaa00',
        },
        {
          label: 'Dense vegetation',
          lowerValue: 0.5,
          upperValue: 1.0,
        },
      ],
    },
    PPI: {
      index: 'PPI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Low Pigment',
          lowerValue: -1.0,
          upperValue: 0.3,
        },
        {
          label: 'Moderate Pigment',
          lowerValue: 0.3,
          upperValue: 0.6,
          valueColor: '#ffaa00',
        },
        {
          label: 'High Pigment',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    CHI_VEGETABLES: {
      index: 'NDVI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label:
            'Low chlorophyll content, stressed vegetables',
          lowerValue: -1.0,
          upperValue: 0.3,
        },
        {
          label: 'Moderate chlorophyll content',
          lowerValue: 0.3,
          upperValue: 0.6,
          valueColor: '#ffaa00',
        },
        {
          label:
            'High chlorophyll content, healthy vegetables',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    CHI_ORCHARDS: {
      index: 'CHI_ORCHARDS',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label:
            'Low chlorophyll content, stressed orchards',
          lowerValue: -1.0,
          upperValue: 0.3,
        },
        {
          label: 'Moderate chlorophyll content',
          lowerValue: 0.3,
          upperValue: 0.6,
          valueColor: '#ffaa00',
        },
        {
          label:
            'High chlorophyll content, healthy orchards',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    NDWI: {
      index: 'NDWI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Very low water content',
          lowerValue: -1.0,
          upperValue: 0.0,
          valueColor: '#7db8da',
        },
        {
          label: 'Low water content',
          lowerValue: 0.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate water content',
          lowerValue: 0.2,
          upperValue: 0.3,
        },
        {
          label: 'High water content',
          lowerValue: 0.3,
          upperValue: 1.0,
        },
      ],
    },
    VWS: {
      index: 'VWS',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Very high water stress',
          lowerValue: -1.0,
          upperValue: 0.0,
          valueColor: '#7db8da',
        },
        {
          label: 'High water stress',
          lowerValue: 0.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate water stress',
          lowerValue: 0.2,
          upperValue: 0.3,
        },
        {
          label: 'Low water stress',
          lowerValue: 0.3,
          upperValue: 1.0,
        },
      ],
    },
    LWCI: {
      index: 'LWCI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Low leaf water content',
          lowerValue: -1.0,
          upperValue: 0.3,
        },
        {
          label: 'Moderate leaf water content',
          lowerValue: 0.3,
          upperValue: 0.6,
        },
        {
          label: ' High leaf water content',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    VNI: {
      index: 'VNI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Low nitrogen content',
          lowerValue: -1.0,
          upperValue: 0.0,
        },
        {
          label: 'Moderate nitrogen content',
          lowerValue: 0.0,
          upperValue: 0.5,
        },
        {
          label: 'High nitrogen content',
          lowerValue: 0.5,
          upperValue: 1.0,
        },
      ],
    },
    BSI: {
      index: 'BSI',
      indexLimits: {
        lower: 0,
        upper: 2,
      },
      indexBreakPoints: [
        {
          label: 'Low barren soil',
          lowerValue: 0.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate barren soil',
          lowerValue: 0.2,
          upperValue: 0.6,
        },
        {
          label: 'High barren soil',
          lowerValue: 0.6,
          upperValue: 2.0,
        },
      ],
    },
    LAI: {
      index: 'LAI',
      indexLimits: {
        lower: 0,
        upper: 6,
      },
      indexBreakPoints: [
        {
          label: 'Sparse leaf area, low vegetation density',
          lowerValue: 0,
          upperValue: 1,
        },
        {
          label:
            'Moderate leaf area, moderate vegetation density',
          lowerValue: 1,
          upperValue: 3,
        },
        {
          label: 'High leaf area, dense vegetation',
          lowerValue: 3,
          upperValue: 4,
        },
        {
          label:
            'Very high leaf area, very dense and robust vegetation',
          lowerValue: 4,
          upperValue: 6,
        },
      ],
    },
    CHI: {
      index: 'CHI',
      indexLimits: {
        lower: 0,
        upper: 6,
      },
      indexBreakPoints: [
        {
          label: 'Very low chlorophyll content',
          lowerValue: 0,
          upperValue: 1,
        },
        {
          label: 'Low chlorophyll content',
          lowerValue: 1,
          upperValue: 2,
        },
        {
          label: 'Moderate chlorophyll content',
          lowerValue: 2,
          upperValue: 3,
        },
        {
          label: 'High chlorophyll content',
          lowerValue: 3,
          upperValue: 4,
        },
        {
          label: 'Very High chlorophyll content',
          lowerValue: 4,
          upperValue: 6,
        },
      ],
    },
    CSI: {
      index: 'CSI',
      indexLimits: {
        lower: 0,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Low Stress',
          lowerValue: 0.0,
          upperValue: 0.2,
        },
        {
          label: 'Moderate Stress',
          lowerValue: 0.2,
          upperValue: 0.5,
          valueColor: '#bde381',
        },
        {
          label: 'High Stress',
          lowerValue: 0.5,
          upperValue: 1.0,
        },
      ],
    },
    MIX_LAI: {
      index: 'MIX_LAI',
      indexLimits: {
        lower: 0,
        upper: 6,
      },
      indexBreakPoints: [
        {
          label: 'Sparse leaf area, low vegetation density',
          lowerValue: 0,
          upperValue: 1,
        },
        {
          label:
            'Moderate leaf area, moderate vegetation density',
          lowerValue: 1,
          upperValue: 3,
        },
        {
          label: 'High leaf area, dense vegetation',
          lowerValue: 3,
          upperValue: 4,
        },
        {
          label:
            'Very high leaf area, very dense and robust vegetation',
          lowerValue: 4,
          upperValue: 6,
        },
      ],
    },
    CRI700: {
      index: 'CRI700',
      indexLimits: {
        lower: -1,
        upper: 15,
      },
      indexBreakPoints: [
        {
          label: 'Low carotenoid content',
          lowerValue: -1,
          upperValue: 3,
        },
        {
          label: 'Moderate carotenoid content',
          lowerValue: 3,
          upperValue: 7,
        },
        {
          label: 'High carotenoid content',
          lowerValue: 7,
          upperValue: 10,
        },
        {
          label: 'Very high carotenoid content',
          lowerValue: 10,
          upperValue: 15,
        },
      ],
    },
    ARI: {
      index: 'ARI',
      indexLimits: {
        lower: -1,
        upper: 15,
      },
      indexBreakPoints: [
        {
          label:
            'Low anthocyanin content, High crop stress',
          lowerValue: -1,
          upperValue: 3,
        },
        {
          label:
            'Moderate anthocyanin content, Moderate crop stress',
          lowerValue: 3,
          upperValue: 7,
        },
        {
          label:
            'High anthocyanin content, Low crop stress',
          lowerValue: 7,
          upperValue: 12,
        },
        {
          label:
            'Very high anthocyanin content, Very low crop stress',
          lowerValue: 12,
          upperValue: 15,
        },
      ],
    },
    SBI: {
      index: 'SBI',
      indexLimits: {
        lower: 0,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label:
            'Darker surfaces (water, dense vegetation)',
          lowerValue: 0.0,
          upperValue: 0.3,
        },
        {
          label:
            'Moderate brightness (soil, sparse vegetation)',
          lowerValue: 0.3,
          upperValue: 0.6,
        },
        {
          label: 'Bright surfaces (urban areas, bare soil)',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
    LLCI: {
      index: 'LLCI',
      indexLimits: {
        lower: -1,
        upper: 1,
      },
      indexBreakPoints: [
        {
          label: 'Low leaf water content',
          lowerValue: -1.0,
          upperValue: 0.3,
        },
        {
          label: 'Moderate leaf water content',
          lowerValue: 0.3,
          upperValue: 0.6,
        },
        {
          label: ' High leaf water content',
          lowerValue: 0.6,
          upperValue: 1.0,
        },
      ],
    },
  };

  return completeIndexMapping[index];
};
